import React from 'react'
import { Grid, Theme } from '@material-ui/core'
import { FormattedMessage, useIntl } from 'react-intl'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import Paper from '@material-ui/core/Paper'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableRow from '@material-ui/core/TableRow'
import TableHead from '@material-ui/core/TableHead'
import TableContainer from '@material-ui/core/TableContainer'
import useFormikHelper from '../useFormikHelper'
import {
  checkForRedesignedBiTubeProduct,
  useProductFromQueryParam,
  biTubeSequencePropertiesTitle,
  biTubeSequencePropertiesNameTitle,
  displayStaticTitleInSequenceProperties,
} from '../ProductsConfiguration'
import { TubeProperties } from '../../../utils/StandardMTOEnum'
import { BiTubeProduct } from '../../BiTube/enum'

const useStyle = makeStyles((theme: Theme) => {
  return {
    title: {
      fontWeight: 700,
      fontSize: theme.typography.pxToRem(16),
    },
    description: {
      fontWeight: 400,
    },
  }
})
const StyledTableRow = withStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.grey,
    borderTop: `1px solid ${theme.palette.grey[400]}`,
    borderBottom: `1px solid ${theme.palette.grey[400]}`,
  },
}))(TableRow)

const OligoProperties = () => {
  const classes = useStyle()
  const intl = useIntl()
  const formik = useFormikHelper()
  const product = useProductFromQueryParam()
  const defaultValue = '-'
  const {
    baseCount,
    molecularWeight,
    secondaryStructure,
    primerDimer,
    meltingTemperature,
    gcContent,
    antisenceName,
    name,
    antisenseBaseCount,
    antisenseMolecularWeight,
    antisensePrimerDimer,
    antisenseMeltingTemperature,
    antisenseGcContent,
    antisenseSecondaryStructure,
  } = formik.selectedOligoValues

  const isBiTubeProduct = checkForRedesignedBiTubeProduct(product)
  const tabRightLeftPadding = isBiTubeProduct ? 4 : 6
  const loadStaticTitle = displayStaticTitleInSequenceProperties(product)

  interface OligoProperty {
    value: string | number | undefined
    isAntiSense: boolean
    loadStaticTitle?: boolean // Optional property
    renderFunction?: (args: {
      value: string | number | undefined
      isAntiSense: boolean
    }) => string
  }

  const StyledTableCell = withStyles((theme) => ({
    root: {
      padding: theme.spacing(2, tabRightLeftPadding),
    },
    head: {
      fontSize: theme.typography.pxToRem(12),
      fontWeight: theme.typography.fontWeightBold,
    },
  }))(TableCell)
  const renderValue = ({
    value,
    isAntiSense,
    loadStaticTitle = false,
  }: {
    value?: string | number
    isAntiSense?: boolean
    loadStaticTitle?: boolean
  }) => {
    const sequenceType = isAntiSense
      ? TubeProperties.AntiSenseSequence
      : TubeProperties.Sequence
    const isEmptySequence = isAntiSense
      ? formik.selectedOligoValues.antisenceSequence === '' ||
        !formik.selectedOligoValues.antisenceSequence
      : formik.selectedOligoValues.sequence === '' ||
        !formik.selectedOligoValues.sequence
    const staticTitle = isAntiSense ? (
      <FormattedMessage id="_PC_SPEC_ANTISENSE" defaultMessage="Antisense" />
    ) : (
      <FormattedMessage id="CONF_BITUBE_SENS" defaultMessage="Sense" />
    )
    return formik.errors?.[sequenceType] || isEmptySequence
      ? defaultValue
      : loadStaticTitle
        ? staticTitle
        : value || defaultValue
  }
  const renderSecondaryStructureValue = ({
    value,
    isAntiSense,
  }: {
    value?: string | number
    isAntiSense?: boolean
  }) => {
    const sequenceType = isAntiSense
      ? TubeProperties.AntiSenseSequence
      : TubeProperties.Sequence

    const isEmptySequence = isAntiSense
      ? formik.selectedOligoValues.antisenceSequence === ''
      : formik.selectedOligoValues.sequence === ''

    return formik.errors?.[sequenceType] || isEmptySequence
      ? defaultValue
      : value
        ? intl.formatMessage({
            id: String(value).toUpperCase(),
            defaultMessage: value as string,
          })
        : defaultValue
  }
  const formatAndCleanMessage = (
    intl,
    messageId: string,
    defaultMessage: string
  ) => {
    return intl
      .formatMessage({
        id: messageId,
        defaultMessage: defaultMessage,
      })
      .replace(/[：:](?:\s*\**)?$/, '')
  }
  const title = biTubeSequencePropertiesTitle(product)
  const biTubeNameTitle = biTubeSequencePropertiesNameTitle(product)
  const senseProperties: OligoProperty[] = [
    { value: baseCount, isAntiSense: false },
    { value: molecularWeight, isAntiSense: false },
    {
      value: secondaryStructure,
      isAntiSense: false,
      renderFunction: renderSecondaryStructureValue,
    },
    { value: primerDimer, isAntiSense: false },
    { value: meltingTemperature, isAntiSense: false },
    { value: gcContent, isAntiSense: false },
  ]

  const antisenseProperties: OligoProperty[] = [
    { value: antisenseBaseCount, isAntiSense: true },
    { value: antisenseMolecularWeight, isAntiSense: true },
    {
      value: antisenseSecondaryStructure,
      isAntiSense: true,
      renderFunction: renderSecondaryStructureValue,
    },
    { value: antisensePrimerDimer, isAntiSense: true },
    { value: antisenseMeltingTemperature, isAntiSense: true },
    { value: antisenseGcContent, isAntiSense: true },
  ]
  // Check if isBiTubeProduct is true and conditionally add entries
  if (isBiTubeProduct) {
    senseProperties.unshift({
      value: name,
      isAntiSense: false,
      loadStaticTitle,
    })
    antisenseProperties.unshift({
      value: antisenceName,
      isAntiSense: true,
      loadStaticTitle,
    })
  }
  const isMirna = product === BiTubeProduct.mirna

  const primaryProperties: OligoProperty[] = isMirna
    ? antisenseProperties
    : senseProperties
  const secondaryProperties: OligoProperty[] = isMirna
    ? senseProperties
    : antisenseProperties

  return (
    <Grid container>
      <Grid item xs={12} className={classes.title}>
        {title}
      </Grid>
      <Grid item xs={12} className={classes.description}>
        <FormattedMessage
          id="CONF_TUBES_OLIGO_PROPERTIES_DESCRIPTION"
          defaultMessage="Data available for download at step 2."
        />
      </Grid>
      <Grid item xs={12} style={{ marginTop: '24px' }}>
        <TableContainer>
          <Paper>
            <Table>
              <TableHead>
                <StyledTableRow>
                  {isBiTubeProduct && (
                    <StyledTableCell>{biTubeNameTitle}</StyledTableCell>
                  )}
                  <StyledTableCell>
                    <FormattedMessage
                      id="CONF_BASES_VIEW_ALL"
                      defaultMessage="# Bases"
                    />
                  </StyledTableCell>
                  <StyledTableCell>
                    <FormattedMessage
                      id="CONF_MOLECULAR_WEIGHT"
                      defaultMessage="Molecular Weight"
                    />
                  </StyledTableCell>
                  <StyledTableCell>
                    <FormattedMessage
                      id="CONF_SECONDARY_STRUCTURE"
                      defaultMessage="Secondary Structure"
                    />
                  </StyledTableCell>
                  <StyledTableCell>
                    <FormattedMessage
                      id="CONF_PRIMER_DIMER"
                      defaultMessage="Primer Dimer"
                    />
                  </StyledTableCell>
                  <StyledTableCell>
                    {formatAndCleanMessage(
                      intl,
                      'CONF_DNAPLATES_MELTING_TEMPERATURE',
                      'Melting Temp (℃)'
                    )}
                  </StyledTableCell>
                  <StyledTableCell>
                    {formatAndCleanMessage(
                      intl,
                      'CONF_DNAPLATES_GC_CONTENT',
                      'GC Content (%)'
                    )}
                  </StyledTableCell>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  {primaryProperties.map(
                    (
                      { value, isAntiSense, loadStaticTitle, renderFunction },
                      index
                    ) => (
                      <StyledTableCell key={index}>
                        {renderFunction
                          ? renderFunction({ value, isAntiSense })
                          : renderValue({
                              value,
                              isAntiSense,
                              loadStaticTitle,
                            })}
                      </StyledTableCell>
                    )
                  )}
                </TableRow>
                {isBiTubeProduct && (
                  <TableRow>
                    {secondaryProperties.map(
                      (
                        { value, isAntiSense, loadStaticTitle, renderFunction },
                        index
                      ) => (
                        <StyledTableCell key={index}>
                          {renderFunction
                            ? renderFunction({ value, isAntiSense })
                            : renderValue({
                                value,
                                isAntiSense,
                                loadStaticTitle,
                              })}
                        </StyledTableCell>
                      )
                    )}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </TableContainer>
      </Grid>
    </Grid>
  )
}

export default OligoProperties
